import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { useRepubContext } from '~/providers/repub-context';

import { REPUB_CONFIG as PLUS_REPUB_CONFIG } from '../repub-config/plus-tools-repub-config';
import type { RepubConfig } from '../repub-config/types';

type PlusConfigContextValue = RepubConfig;

export const PlusConfigContext = createContext<PlusConfigContextValue | undefined>(undefined);
PlusConfigContext.displayName = 'PlusConfigContext';

/**
 * Provides Plus configuration based on the current republic.
 * Must be used inside RepubProvider.
 */
export const PlusConfigProvider = ({ children }: PropsWithChildren) => {
  const { repub } = useRepubContext();

  const config = useMemo(() => {
    const plusConfig = PLUS_REPUB_CONFIG[repub];
    if (!plusConfig) {
      throw new Error(`Plus config not found for repub: ${repub}`);
    }
    return plusConfig;
  }, [repub]);

  return <PlusConfigContext.Provider value={config}>{children}</PlusConfigContext.Provider>;
};

/**
 * Hook to access Plus configuration.
 * Must be used inside PlusConfigProvider.
 */
export const usePlusConfig = () => {
  const context = useContext(PlusConfigContext);
  if (!context) {
    throw new Error('usePlusConfig must be used inside PlusConfigProvider');
  }
  return context;
};
